// Editorial Atelier design tokens — shared quiet-luxury visual language.
// See ProductPage.js / ProductListing.js for the canonical usage.

export const NAVY = '#303e70';
export const NAVY_DEEP = '#1a2550';
export const LIGHT = '#b6c7e6';       // light brand blue — accents on navy / hairline highlights
export const PAPER = '#faf8f3';       // warm ivory canvas
export const PAPER_SOFT = '#f1ece2';  // image wells / inactive fills
export const PAPER_BLUE = '#eef1f7';  // cool blue-tinted wash — alternating sections
export const INK = '#33343a';         // warm near-black body text
export const MUTED = '#7c7b78';       // warm grey captions
export const LINE = 'rgba(48,62,112,0.12)';
export const LINE_STRONG = 'rgba(48,62,112,0.26)';
export const WHATSAPP = '#25D366';

// The fixed app bar's translucent navy base (non-condensed) as bare "r, g, b"
// channels. Single source of truth so the bar and any surface that must read
// as an extension of it (the Lookbook page top) stay locked together: the bar
// paints rgba(APPBAR_NAVY_RGB, 0.8), and rgba(x,0.8) over solid rgb(x) === x,
// so a page top of rgb(APPBAR_NAVY_RGB) has no seam under the bar. See
// AppAppBar.js (non-condensed background) and Lookbook.js.
export const APPBAR_NAVY_RGB = '30, 40, 67';

// Motion — the single editorial easing shared by every reveal/settle on the
// site (already used by the tile hover zoom and fade.css). New animation work
// should use this rather than introducing another curve.
export const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Curtain reveal — editorial top-to-bottom wipe for images entering the
// viewport. Spread into sx and drive `visible` from useFadeIn. Vertical
// insets only, so stylis-plugin-rtl has nothing to flip. `delay` staggers
// sibling frames (pass e.g. `${i * 120}ms`).
export const curtainReveal = (visible, delay = '0ms') => ({
  clipPath: visible ? 'inset(0 0 0% 0)' : 'inset(0 0 100% 0)',
  transition: `clip-path 0.9s ${EASE} ${delay}`,
  '@media (prefers-reduced-motion: reduce)': {
    clipPath: 'none',
    transition: 'none',
  },
});

// Companion to curtainReveal, for the image layer INSIDE the clipped frame:
// the photograph starts slightly oversized and relaxes to rest as the curtain
// opens. Deliberately slower than the wipe (1.6s vs 0.9s) so the frame keeps
// settling for a beat after the curtain lands. Put it on a dedicated layer —
// never on an element whose transform is already driven per-frame (e.g. the
// parallax img), or the transition will rubber-band that motion.
export const curtainSettle = (visible, delay = '0ms') => ({
  transform: visible ? 'scale(1)' : 'scale(1.06)',
  transition: `transform 1.6s ${EASE} ${delay}`,
  '@media (prefers-reduced-motion: reduce)': {
    transform: 'none',
    transition: 'none',
  },
});

// Ambient drift — endless, near-imperceptible breathe (1 → 1.015 over 20s)
// on editorial imagery, so a frame stays alive after its entrance settles.
// `alternate` makes the loop seamless. Needs its OWN layer between the
// curtainSettle layer and the image: settle's transform is a one-shot
// transition and the campaign img's transform is driven per-frame by the
// parallax, so sharing an element with either would fight their transforms.
// Pass a negative `phase` (e.g. '-20s', half the 40s alternate cycle) to
// counter-phase sibling frames — one drifts out while the other drifts back.
export const ambientDrift = (phase = '0s') => ({
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `ambientDrift 20s ease-in-out ${phase} infinite alternate`,
  },
  '@keyframes ambientDrift': {
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(1.015)' },
  },
});

// Lead paragraph under a SectionOpener — muted editorial intro copy.
export const lede = {
  fontFamily: 'Lato, sans-serif',
  fontSize: { xs: '0.95rem', md: '1rem' },
  lineHeight: 1.9,
  color: MUTED,
};

// Standard editorial body copy inside sections — ink, slightly tighter.
export const bodyEditorial = {
  fontFamily: 'Lato, sans-serif',
  fontSize: '0.95rem',
  lineHeight: 1.85,
  color: INK,
};

// Uppercase, letter-spaced eyebrow / micro-label text.
export const microLabel = {
  fontFamily: 'Lato, sans-serif',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
};

// Responsive section rhythm used across the homepage.
export const sectionSpacing = Object.freeze({
  compact: Object.freeze({ xs: 6, md: 10 }),
  standard: Object.freeze({ xs: 7, md: 11 }),
  generous: Object.freeze({ xs: 8, md: 12 }),
});

// Shared square field treatment for TextField, FormControl, and Select.
export const atelierFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: PAPER,
    color: INK,
    fontFamily: 'Lato, sans-serif',
    '& fieldset': { borderColor: LINE },
    '&:hover fieldset': { borderColor: LINE_STRONG },
    '&.Mui-focused fieldset': { borderColor: NAVY },
  },
  '& .MuiInputLabel-root': {
    color: MUTED,
    fontFamily: 'Lato, sans-serif',
    '&.Mui-focused': { color: NAVY },
  },
};

export const atelierSectionHeading = {
  fontFamily: 'Playfair Display, serif',
  fontWeight: 500,
  color: NAVY_DEEP,
};

export const atelierPrimaryButton = {
  ...microLabel,
  borderRadius: 0,
  px: 3.5,
  py: 1.4,
  color: PAPER,
  bgcolor: NAVY,
  '&:hover': { bgcolor: NAVY_DEEP },
};

export const atelierSecondaryButton = {
  ...microLabel,
  borderRadius: 0,
  px: 3.5,
  py: 1.4,
  color: NAVY,
  bgcolor: PAPER,
  border: `1px solid ${LINE_STRONG}`,
  '&:hover': {
    bgcolor: PAPER_SOFT,
    borderColor: NAVY,
  },
};
